import type { PaymentMethod } from '@bazaar/shared';

export interface CartLine {
  productId: number;
  name: string;
  code: string;
  thumbUrl: string | null;
  /** The product's catalogue price, used to detect and reset overrides. */
  defaultPriceCents: number;
  /** The price actually charged for this line (catalogue price, or an override). */
  unitPriceCents: number;
  qty: number;
  /** Most recently known available stock for this product. */
  stockQty: number;
}

export interface CartState {
  lines: CartLine[];
  discountCents: number;
  /**
   * False while the discount field holds a draft that doesn't parse to a
   * valid amount, or one that (at the moment it was typed) exceeded the
   * subtotal - see `SET_DISCOUNT` below. Kept separate from `discountCents`
   * itself (which is left at its last-committed value, not reset to 0) so
   * a bad draft's text keeps showing in the input for the user to correct,
   * while `computeTotals` only ever reflects a discount that is actually
   * in effect. Otherwise, replacing a valid discount with an invalid one
   * left the *previous* valid discount in the totals display even though
   * it no longer matched what was in the box - see the QA reverify
   * report's new-finding "Invalid discount leaves the previous valid
   * discount in the totals block".
   */
  discountInputValid: boolean;
  paymentMethod: PaymentMethod;
  note: string;
}

export const initialCartState: CartState = {
  lines: [],
  discountCents: 0,
  discountInputValid: true,
  paymentMethod: 'cash',
  note: '',
};

export interface CartProductInput {
  id: number;
  name: string;
  code: string;
  thumbUrl: string | null;
  priceCents: number;
  stockQty: number;
}

export type CartAction =
  | { type: 'ADD'; product: CartProductInput }
  | { type: 'INCREMENT'; productId: number }
  | { type: 'DECREMENT'; productId: number }
  | { type: 'REMOVE'; productId: number }
  | { type: 'SET_QTY'; productId: number; qty: number }
  | { type: 'SET_PRICE_OVERRIDE'; productId: number; cents: number | null }
  | { type: 'SET_DISCOUNT'; cents: number | null }
  | { type: 'SET_PAYMENT_METHOD'; method: PaymentMethod }
  | { type: 'SET_NOTE'; note: string }
  | { type: 'UPDATE_STOCK'; productId: number; stockQty: number }
  | { type: 'CLEAR' }
  | { type: 'REPLACE'; state: CartState };

function clamp(value: number, lo: number, hi: number): number {
  return Math.min(hi, Math.max(lo, value));
}

export function cartReducer(state: CartState, action: CartAction): CartState {
  switch (action.type) {
    case 'ADD': {
      const { product } = action;
      if (product.stockQty <= 0) return state;
      const existing = state.lines.find((l) => l.productId === product.id);
      if (existing) {
        if (existing.qty >= product.stockQty) {
          return { ...state, lines: state.lines.map((l) => (l.productId === product.id ? { ...l, stockQty: product.stockQty } : l)) };
        }
        return {
          ...state,
          lines: state.lines.map((l) =>
            l.productId === product.id ? { ...l, qty: l.qty + 1, stockQty: product.stockQty } : l,
          ),
        };
      }
      const line: CartLine = {
        productId: product.id,
        name: product.name,
        code: product.code,
        thumbUrl: product.thumbUrl,
        defaultPriceCents: product.priceCents,
        unitPriceCents: product.priceCents,
        qty: 1,
        stockQty: product.stockQty,
      };
      return { ...state, lines: [...state.lines, line] };
    }

    case 'INCREMENT':
      return {
        ...state,
        lines: state.lines.map((l) =>
          l.productId === action.productId ? { ...l, qty: Math.min(l.qty + 1, l.stockQty) } : l,
        ),
      };

    case 'DECREMENT': {
      const line = state.lines.find((l) => l.productId === action.productId);
      if (!line) return state;
      if (line.qty <= 1) {
        return { ...state, lines: state.lines.filter((l) => l.productId !== action.productId) };
      }
      return {
        ...state,
        lines: state.lines.map((l) => (l.productId === action.productId ? { ...l, qty: l.qty - 1 } : l)),
      };
    }

    case 'REMOVE':
      return { ...state, lines: state.lines.filter((l) => l.productId !== action.productId) };

    case 'SET_QTY': {
      if (action.qty <= 0) {
        return { ...state, lines: state.lines.filter((l) => l.productId !== action.productId) };
      }
      return {
        ...state,
        lines: state.lines.map((l) =>
          l.productId === action.productId ? { ...l, qty: clamp(action.qty, 1, l.stockQty) } : l,
        ),
      };
    }

    case 'SET_PRICE_OVERRIDE':
      return {
        ...state,
        lines: state.lines.map((l) =>
          l.productId === action.productId
            ? { ...l, unitPriceCents: action.cents === null ? l.defaultPriceCents : Math.max(0, action.cents) }
            : l,
        ),
      };

    case 'SET_DISCOUNT':
      if (action.cents === null) {
        // Don't touch discountCents - the input's own text is driven by the
        // caller's local draft state, not by this value, so there's nothing
        // useful to reset it to. Just flag the discount as not currently in
        // effect so totals stop showing it.
        return { ...state, discountInputValid: false };
      }
      return { ...state, discountCents: Math.max(0, Math.trunc(action.cents)), discountInputValid: true };

    case 'SET_PAYMENT_METHOD':
      return { ...state, paymentMethod: action.method };

    case 'SET_NOTE':
      return { ...state, note: action.note };

    case 'UPDATE_STOCK': {
      const lines = state.lines
        .map((l) =>
          l.productId === action.productId
            ? { ...l, stockQty: action.stockQty, qty: Math.min(l.qty, action.stockQty) }
            : l,
        )
        .filter((l) => l.qty > 0);
      return { ...state, lines };
    }

    case 'CLEAR':
      return { ...initialCartState };

    case 'REPLACE':
      return action.state;

    default:
      return state;
  }
}

export interface CartTotals {
  subtotalCents: number;
  discountCents: number;
  totalCents: number;
  itemCount: number;
}

export function computeTotals(state: CartState): CartTotals {
  const subtotalCents = state.lines.reduce((sum, l) => sum + l.unitPriceCents * l.qty, 0);
  // An invalid draft (unparsable text, or a value that no longer fits the
  // current subtotal) displays as "no discount applied", not as whatever
  // discount was last actually committed - see `discountInputValid` above.
  const effectiveDiscountCents = state.discountInputValid ? state.discountCents : 0;
  const discountCents = Math.min(effectiveDiscountCents, subtotalCents);
  const totalCents = Math.max(0, subtotalCents - discountCents);
  const itemCount = state.lines.reduce((sum, l) => sum + l.qty, 0);
  return { subtotalCents, discountCents, totalCents, itemCount };
}

/** Whether the current discount is valid to submit: a valid draft that's also <= the true subtotal. */
export function isDiscountValid(state: CartState): boolean {
  if (!state.discountInputValid) return false;
  const subtotalCents = state.lines.reduce((sum, l) => sum + l.unitPriceCents * l.qty, 0);
  return state.discountCents <= subtotalCents;
}
